import { useMemo } from 'react';
import { useRackScriptResolution } from './useRackScriptResolution';
import { useRackAssignmentsQuery } from '@api/bff/rackAssignments/useRackAssignments';
import { formatPositionLabel } from '../components/VoltmeterRack/utils';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models';

export type RackReadiness = {
  occupiedCount: number;
  resolvedCount: number;
  unresolvedCount: number;
  isReady: boolean;
};

export const useRackReadiness = (
  jobNumber: string,
  details: WorkOrderDetailViewModel[]
): RackReadiness => {
  const { resolutionBySequence } = useRackScriptResolution(details);
  const { data: persisted } = useRackAssignmentsQuery(jobNumber, !!jobNumber);

  return useMemo(() => {
    const persistedByCell = new Map(
      (persisted?.cells ?? []).map(cell => [cell.cell, cell])
    );

    const occupied = details.filter(
      detail =>
        detail.sequenceNumber != null && (detail.id ?? '').trim() !== ''
    );

    let resolvedCount = 0;
    occupied.forEach(detail => {
      const sequenceNumber = detail.sequenceNumber as number;
      const auto = resolutionBySequence.get(sequenceNumber)?.resolvedScript ?? null;
      const persistedCell = persistedByCell.get(
        formatPositionLabel(sequenceNumber)
      );
      const isResolved =
        auto !== null ||
        (persistedCell != null && persistedCell.scriptGlobalId.trim() !== '');
      if (isResolved) resolvedCount += 1;
    });

    const occupiedCount = occupied.length;

    return {
      occupiedCount,
      resolvedCount,
      unresolvedCount: occupiedCount - resolvedCount,
      isReady: occupiedCount > 0 && resolvedCount === occupiedCount
    };
  }, [details, resolutionBySequence, persisted]);
};
